import { CheckIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/shared/lib'

type OptionTileProps = {
  label: string
  /** One short line. Two words to a short sentence, never a paragraph. */
  description?: string
  icon?: ReactNode
  isSelected?: boolean
  /** `radio` for one-of-many, `checkbox` for independent options. */
  kind?: 'radio' | 'checkbox'
  onSelect?: () => void
  disabled?: boolean
  className?: string
}

/**
 * A setting as a tile instead of a row.
 *
 * A switch row is the right shape for a dense list of toggles; it is the wrong
 * shape for two or three important choices on an otherwise short page, where a
 * row of tiles fills the width honestly and gives each option room for an icon
 * and a line of explanation. Place three or four per row on the 12-column grid.
 */
export function OptionTile({
  label,
  description,
  icon,
  isSelected = false,
  kind = 'checkbox',
  onSelect,
  disabled,
  className,
}: OptionTileProps) {
  return (
    <button
      type="button"
      role={kind}
      aria-checked={isSelected}
      disabled={disabled}
      data-slot="option-tile"
      onClick={onSelect}
      className={cn(
        // These arrive fifteen to a grid, which is the argument for a transform
        // rather than anything that occupies space: the tile pulls away from
        // its own border and every neighbour stays exactly where it was.
        'group/tile press lift flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left outline-none',
        'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-primary/13',
        'disabled:cursor-not-allowed disabled:border-border-disabled disabled:text-fg-disabled',
        // Both states hover, and that is the fix rather than a flourish: the
        // hover used to live in the unselected branch alone, so a grid where
        // everything was chosen — the normal state of this control — answered
        // the pointer with nothing at all. A chosen tile brightens its own
        // accent instead of stepping up the neutral ladder, because on a tinted
        // surface the neutral step is invisible.
        isSelected
          ? 'border-primary/35 bg-primary/8 hover:border-primary/55 hover:bg-primary/14'
          : 'border-border bg-surface-1 hover:border-border-strong hover:bg-surface-2',
        className,
      )}
    >
      {icon !== undefined && (
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-icon-md',
            isSelected ? 'bg-primary/14 text-primary' : 'bg-surface-2 text-fg-secondary',
          )}
        >
          {icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium text-foreground">{label}</span>
        {description !== undefined && (
          <span className="block truncate text-caption text-muted-foreground">{description}</span>
        )}
      </span>

      {/* The indicator is the only thing that says «chosen» — the tint alone is
       * too quiet at a glance, and a tint plus a border plus a check is noise. */}
      <span
        aria-hidden
        className={cn(
          'flex size-4.5 shrink-0 items-center justify-center rounded-full border transition-colors [&_svg]:size-3',
          isSelected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border-strong bg-surface-sunken text-transparent',
        )}
      >
        <CheckIcon strokeWidth={2.5} />
      </span>
    </button>
  )
}
